import React, { useMemo, useState } from 'react';

import styles from './styles.module.css';
import EmptyData from '../../Form/EmptyData';
import { DataTableColumn } from '../index';
import ChevronIcon from '@/assets/icons/chevron-accordion.svg?react';

export interface NestedAccordionTurbine<T = any> {
  id: string;
  title: string;
  subtitle?: string;
  rows: T[];
}

export interface NestedAccordionLocation<T = any> {
  id: string;
  title: string;
  subtitle?: string;
  turbines: NestedAccordionTurbine<T>[];
}

export interface NestedAccordionTableProps<T = any> {
  columns: DataTableColumn<T>[];
  groups: NestedAccordionLocation<T>[];
  className?: string;
  emptyLabel?: string;
  defaultExpandedLocations?: boolean;
  defaultExpandedTurbines?: boolean;
}

function getColumnStyle<T>(column: DataTableColumn<T>): React.CSSProperties {
  if (column.width === 'auto' || column.width === undefined) {
    return { flex: '1 1 0', minWidth: '100px' };
  }
  if (typeof column.width === 'string' && column.width.includes('%')) {
    return { width: column.width };
  }
  return { flex: '1 1 0', minWidth: column.width };
}

function getCellValue<T extends Record<string, any>>(
  row: T,
  column: DataTableColumn<T>
) {
  const fieldValue = row[column?.field || ''];
  const value = column.valueGetter
    ? column.valueGetter(fieldValue, row)
    : fieldValue;

  if (column.renderCell) {
    return column.renderCell({ value, row, field: column?.field || '' });
  }
  return value;
}

function NestedAccordionTable<T extends Record<string, any>>({
  columns,
  groups,
  className,
  emptyLabel = 'No data available',
  defaultExpandedLocations = true,
  defaultExpandedTurbines = true
}: NestedAccordionTableProps<T>) {
  const initialLocations = useMemo(() => {
    const state: Record<string, boolean> = {};
    groups.forEach(location => {
      state[location.id] = defaultExpandedLocations;
    });
    return state;
  }, [groups, defaultExpandedLocations]);

  const initialTurbines = useMemo(() => {
    const state: Record<string, boolean> = {};
    groups.forEach(location => {
      location.turbines.forEach(turbine => {
        state[`${location.id}-${turbine.id}`] = defaultExpandedTurbines;
      });
    });
    return state;
  }, [groups, defaultExpandedTurbines]);

  const [openLocations, setOpenLocations] =
    useState<Record<string, boolean>>(initialLocations);
  const [openTurbines, setOpenTurbines] =
    useState<Record<string, boolean>>(initialTurbines);

  const toggleLocation = (locationId: string) => {
    setOpenLocations(prev => ({ ...prev, [locationId]: !prev[locationId] }));
  };

  const toggleTurbine = (turbineKey: string) => {
    setOpenTurbines(prev => ({ ...prev, [turbineKey]: !prev[turbineKey] }));
  };

  return (
    <div className={styles.scrollArea}>
      <div className={`${styles.container} ${className || ''}`} role="table">
        <div className={styles.headerRow} role="row">
          {columns.map((column, index) => (
            <div
              key={String(column.field ?? index)}
              className={styles.headerCell}
              style={getColumnStyle(column)}
              role="columnheader"
            >
              <span className={styles.headerText}>{column.headerName}</span>
              {column.headerIcon}
            </div>
          ))}
        </div>

        {groups.length === 0 ? (
          <EmptyData label={emptyLabel} />
        ) : (
          groups.map(location => {
            const isLocationOpen = openLocations[location.id];
            return (
              <div key={location.id} className={styles.locationGroup}>
                <button
                  type="button"
                  id={`nestedAccordionLocation-${location.id}`}
                  className={styles.locationHeader}
                  aria-expanded={isLocationOpen}
                  onClick={() => toggleLocation(location.id)}
                >
                  <span className={styles.groupTitleWrapper}>
                    <span className={styles.locationTitle}>
                      {location.title}
                    </span>
                    {location.subtitle && (
                      <span className={styles.groupSubtitle}>
                        {location.subtitle}
                      </span>
                    )}
                  </span>
                  <ChevronIcon
                    className={`${styles.chevron} ${isLocationOpen ? styles.chevronOpen : ''}`}
                    aria-hidden="true"
                  />
                </button>

                {isLocationOpen &&
                  location.turbines.map(turbine => {
                    const turbineKey = `${location.id}-${turbine.id}`;
                    const isTurbineOpen = openTurbines[turbineKey];
                    return (
                      <div key={turbineKey} className={styles.turbineGroup}>
                        <button
                          type="button"
                          id={`nestedAccordionTurbine-${turbineKey}`}
                          className={styles.turbineHeader}
                          aria-expanded={isTurbineOpen}
                          onClick={() => toggleTurbine(turbineKey)}
                        >
                          <span className={styles.groupTitleWrapper}>
                            <span className={styles.turbineTitle}>
                              {turbine.title}
                            </span>
                            {turbine.subtitle && (
                              <span className={styles.groupSubtitle}>
                                {turbine.subtitle}
                              </span>
                            )}
                          </span>
                          <ChevronIcon
                            className={`${styles.chevron} ${isTurbineOpen ? styles.chevronOpen : ''}`}
                            aria-hidden="true"
                          />
                        </button>

                        {isTurbineOpen &&
                          (turbine.rows.length === 0 ? (
                            <EmptyData label={emptyLabel} />
                          ) : (
                            turbine.rows.map((row, rowIndex) => (
                              <div
                                key={row.id ?? rowIndex}
                                className={`${styles.dataRow} ${rowIndex % 2 === 0 ? styles.evenRow : styles.oddRow}`}
                                role="row"
                              >
                                {columns.map((column, colIndex) => (
                                  <div
                                    key={String(column.field ?? colIndex)}
                                    className={styles.dataCell}
                                    style={getColumnStyle(column)}
                                    role="cell"
                                  >
                                    {getCellValue(row, column)}
                                  </div>
                                ))}
                              </div>
                            ))
                          ))}
                      </div>
                    );
                  })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default NestedAccordionTable;
